import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/numberUtils';
import {
  NfaEvaluationResponse,
  NfaEvaluationRow,
  NfaSendRecord,
} from '../../helpers/nfaMailingData';

const HEADER_SX = {
  backgroundColor: '#e0e0e0 !important',
  color: 'black',
  fontWeight: 'bold',
};

const sourceTypeChip = (sourceType: NfaEvaluationRow['source_type']) => {
  switch (sourceType) {
    case 'plx':
      return <Chip label="PLX" size="small" color="secondary" />;
    case 'webinar':
      return <Chip label="Webinar" size="small" color="primary" />;
    case 'kme':
      return <Chip label="KME" size="small" color="info" />;
    case 'vnr':
      return <Chip label="VNR" size="small" color="warning" />;
    default:
      return <Chip label={sourceType} size="small" />;
  }
};

const statusChip = (status: NfaEvaluationRow['status']) => {
  switch (status) {
    case 'confirmed':
      return <Chip label="Gesendet" size="small" color="success" />;
    case 'prepared':
      return <Chip label="Vorbereitet" size="small" color="warning" />;
    case 'cancelled':
      return <Chip label="Abgebrochen" size="small" color="default" />;
    case 'orphan':
      return <Chip label="Nicht gespeichert" size="small" color="default" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const Kpi: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={2.4}>
    <Card>
      <CardContent>
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
);

const EvaluationTab: FC = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(90, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<NfaEvaluationResponse | null>(null);
  const [selectedRow, setSelectedRow] = useState<NfaEvaluationRow | null>(null);
  const [selectedSendDetail, setSelectedSendDetail] = useState<NfaSendRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleRowClick = async (row: NfaEvaluationRow) => {
    setSelectedRow(row);
    setSelectedSendDetail(null);
    if (row.nfa_send_id != null) {
      setDetailLoading(true);
      const result = await fetchData<NfaSendRecord>(`/api/nfa-mailings/sends/${row.nfa_send_id}`);
      setDetailLoading(false);
      if (result.success && result.data) {
        setSelectedSendDetail(result.data);
      }
    }
  };

  const closeDialog = () => {
    setSelectedRow(null);
    setSelectedSendDetail(null);
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const start = startDate.format('YYYY-MM-DD');
      const end = endDate.format('YYYY-MM-DD');
      const result = await fetchData<NfaEvaluationResponse>(
        `/api/nfa-mailings/evaluation?start_date=${start}&end_date=${end}`,
      );
      if (cancelled) return;
      if (result.success && result.data) {
        setData(result.data);
      } else {
        setError(result.error || 'Fehler beim Laden');
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box mb={3} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <DatePicker
          label="Start-Datum"
          value={startDate}
          onChange={(v) => setStartDate(v)}
          format="DD.MM.YYYY"
          slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
        />
        <DatePicker
          label="End-Datum"
          value={endDate}
          onChange={(v) => setEndDate(v)}
          format="DD.MM.YYYY"
          slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
        />
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Kpi label="Mailings" value={formatNumber(data.summary.total_sends, 0)} />
            <Kpi
              label="Registrierungen"
              value={formatNumber(data.summary.total_registrations, 0)}
            />
            <Kpi label="Bestellungen" value={formatNumber(data.summary.total_orders, 0)} />
            <Kpi label="ANFOs" value={formatNumber(data.summary.total_anfos, 0)} />
            <Kpi label="CPA" value={formatNumber(data.summary.total_cpa_eur, 2) + ' €'} />
          </Grid>

          {data.sends.length === 0 ? (
            <Alert severity="info">Keine NFA-Mailings im Zeitraum</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 700 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEADER_SX}>Datum</TableCell>
                    <TableCell sx={HEADER_SX}>Typ</TableCell>
                    <TableCell sx={HEADER_SX}>Betreff / Banner</TableCell>
                    <TableCell sx={HEADER_SX}>Status</TableCell>
                    <TableCell sx={HEADER_SX} align="right">
                      Registrierungen
                    </TableCell>
                    <TableCell sx={HEADER_SX} align="right">
                      Bestellungen
                    </TableCell>
                    <TableCell sx={HEADER_SX} align="right">
                      ANFOs
                    </TableCell>
                    <TableCell sx={HEADER_SX} align="right">
                      CPA €
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.sends.map((row) => (
                    <TableRow
                      key={row.banner}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(row)}
                    >
                      <TableCell>{dayjs(row.planned_send_date).format('DD.MM.YYYY')}</TableCell>
                      <TableCell>{sourceTypeChip(row.source_type)}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.subject || row.banner}
                      >
                        {row.subject || (
                          <Typography variant="body2" fontFamily="monospace" color="textSecondary">
                            {row.banner}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{statusChip(row.status)}</TableCell>
                      <TableCell align="right">{formatNumber(row.registration_count, 0)}</TableCell>
                      <TableCell align="right">{formatNumber(row.order_count, 0)}</TableCell>
                      <TableCell align="right">{formatNumber(row.anfo_count, 0)}</TableCell>
                      <TableCell align="right">{formatNumber(row.cpa_eur, 2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                      Insgesamt
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(data.summary.total_registrations, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(data.summary.total_orders, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(data.summary.total_anfos, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(data.summary.total_cpa_eur, 2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={!!selectedRow} onClose={closeDialog} maxWidth="lg" fullWidth>
        {selectedRow && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ flex: 1, mr: 2 }}>
                  {selectedRow.subject || selectedRow.banner}
                </Typography>
                <IconButton onClick={closeDialog}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                {sourceTypeChip(selectedRow.source_type)}
                {statusChip(selectedRow.status)}
                <Typography variant="body2" color="textSecondary">
                  Datum: {dayjs(selectedRow.planned_send_date).format('DD.MM.YYYY')}
                </Typography>
                <Typography variant="body2" color="textSecondary" fontFamily="monospace">
                  Banner: {selectedRow.banner}
                </Typography>
              </Box>

              <Box display="flex" gap={3} mb={2} flexWrap="wrap">
                <Typography variant="body2">
                  <strong>Registrierungen:</strong>{' '}
                  {formatNumber(selectedRow.registration_count, 0)}
                </Typography>
                <Typography variant="body2">
                  <strong>Bestellungen:</strong> {formatNumber(selectedRow.order_count, 0)}
                </Typography>
                <Typography variant="body2">
                  <strong>ANFOs:</strong> {formatNumber(selectedRow.anfo_count, 0)}
                </Typography>
                <Typography variant="body2">
                  <strong>CPA:</strong> {formatNumber(selectedRow.cpa_eur, 2)} €
                </Typography>
              </Box>

              {selectedRow.status === 'orphan' ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Dieses Mailing wurde nicht über das Dashboard gespeichert — kein HTML-Preview
                  verfügbar. Attribution erfolgt ausschließlich über den Banner-String.
                </Alert>
              ) : detailLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : selectedSendDetail && selectedSendDetail.final_html ? (
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mt: 2,
                  }}
                >
                  <iframe
                    title="Send Preview"
                    srcDoc={selectedSendDetail.final_html}
                    style={{ width: '100%', height: '600px', border: 'none' }}
                  />
                </Box>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Kein HTML für diesen Send gespeichert.
                </Alert>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </LocalizationProvider>
  );
};

export default EvaluationTab;
